<template>
  <div>
    <!-- pt-3: タブ内埋め込み時に日付フィールドの浮きラベルが上要素と被らないための余白 -->
    <div class="d-flex align-center flex-wrap mb-4 pt-3 gap-3">
      <span class="text-h6 mr-2">予約一覧</span>

      <!-- 期間: from -->
      <v-menu
        v-model="fromMenu"
        :close-on-content-click="false"
        transition="scale-transition"
        offset-y
        min-width="auto"
      >
        <template #activator="{ on, attrs }">
          <v-text-field
            :value="dateFrom"
            label="開始日"
            :prepend-icon="icons.mdiCalendar"
            readonly
            dense
            hide-details
            style="max-width: 170px"
            v-bind="attrs"
            v-on="on"
          />
        </template>
        <v-date-picker
          v-model="dateFrom"
          locale="ja"
          :day-format="date => new Date(date).getDate()"
          @input="fromMenu = false"
        />
      </v-menu>

      <span>〜</span>

      <!-- 期間: to -->
      <v-menu
        v-model="toMenu"
        :close-on-content-click="false"
        transition="scale-transition"
        offset-y
        min-width="auto"
      >
        <template #activator="{ on, attrs }">
          <v-text-field
            :value="dateTo"
            label="終了日"
            :prepend-icon="icons.mdiCalendar"
            readonly
            dense
            hide-details
            style="max-width: 170px"
            v-bind="attrs"
            v-on="on"
          />
        </template>
        <v-date-picker
          v-model="dateTo"
          locale="ja"
          :day-format="date => new Date(date).getDate()"
          @input="toMenu = false"
        />
      </v-menu>

      <v-spacer />

      <!-- 指定日の予約管理へ移動（新規予約作成用） -->
      <v-menu
        v-model="gotoMenu"
        :close-on-content-click="false"
        transition="scale-transition"
        offset-y
        min-width="auto"
      >
        <template #activator="{ on, attrs }">
          <v-btn
            small
            color="primary"
            class="mr-2"
            v-bind="attrs"
            v-on="on"
          >
            <v-icon
              small
              left
            >
              {{ icons.mdiCalendarPlus }}
            </v-icon>
            指定日の日別予約へ
          </v-btn>
        </template>
        <v-date-picker
          locale="ja"
          :day-format="date => new Date(date).getDate()"
          @input="goToTimeline"
        />
      </v-menu>

      <v-btn
        small
        outlined
        color="primary"
        :to="{ name: 'waiter-reservations', params: { club_slug: $route.params.club_slug } }"
      >
        <v-icon
          small
          left
        >
          {{ icons.mdiCalendarClock }}
        </v-icon>
        日別予約へ
      </v-btn>
    </div>

    <v-skeleton-loader
      v-if="isLoading"
      type="table"
    />

    <v-data-table
      v-else
      :headers="headers"
      :items="tableItems"
      :items-per-page="50"
      :footer-props="{ itemsPerPageOptions: [25, 50, 100, -1] }"
      sort-by="sortKey"
      no-data-text="この期間に予約はありません"
      class="elevation-1"
      @click:row="onRowClick"
    >
      <template #[`item.date`]="{ item }">
        {{ item.date }}
      </template>
      <template #[`item.status`]="{ item }">
        <v-chip
          :color="item.statusColor"
          small
          dark
          label
        >
          {{ item.statusLabel }}
        </v-chip>
      </template>
      <template #[`item.actions`]="{ item }">
        <v-btn
          v-if="item.status === 'confirmed' && item.reservedDate === businessToday"
          x-small
          color="success"
          :loading="checkingInId === item.id"
          @click.stop="checkIn(item)"
        >
          <v-icon
            x-small
            left
          >
            {{ icons.mdiSofaSingle }}
          </v-icon>
          卓作成
        </v-btn>
      </template>
    </v-data-table>

    <v-snackbar
      v-model="snackbar"
      color="error"
      timeout="4000"
    >
      {{ snackbarMessage }}
    </v-snackbar>

    <v-snackbar
      v-model="successSnackbar"
      color="success"
      timeout="2000"
    >
      卓を作成しました
    </v-snackbar>
  </div>
</template>

<script>
import {
  mdiCalendar, mdiCalendarPlus, mdiCalendarClock, mdiSofaSingle,
} from '@mdi/js'
import ReservationApi from '@/api/waiter/Reservation'
import { checkInMissingItems } from '@/views/waiter/reservations/reservationDisplayUtils'
import { getBusinessTodayStr } from '@/views/waiter/floorUtils'
import useCurrentData from '@/views/composable/useCurrentData'

const STATUS_COLOR = {
  tentative: 'grey',
  confirmed: 'primary',
  checked_in: 'success',
  completed: 'grey lighten-1',
  canceled: 'error',
}

const STATUS_LABEL = {
  tentative: '仮予約',
  confirmed: '確定',
  checked_in: '来店中',
  completed: '完了',
  canceled: 'キャンセル',
}

const WEEKDAYS = ['日', '月', '火', '水', '木', '金', '土']

export default {
  name: 'ReservationList',
  data() {
    return {
      icons: {
        mdiCalendar, mdiCalendarPlus, mdiCalendarClock, mdiSofaSingle,
      },
      dateFrom: this.todayOffset(0),
      dateTo: this.todayOffset(30),
      fromMenu: false,
      toMenu: false,
      gotoMenu: false,
      reservations: [],
      isLoading: false,
      checkingInId: null,
      snackbar: false,
      snackbarMessage: '',
      successSnackbar: false,
      headers: [
        { text: '日付', value: 'date' },
        { text: '時間', value: 'time' },
        { text: 'ホール / 卓', value: 'table' },
        { text: '人数', value: 'count', align: 'end' },
        { text: '指名', value: 'nominated' },
        { text: 'コース', value: 'course' },
        { text: '顧客', value: 'customers' },
        { text: 'ステータス', value: 'status' },
        {
          text: '', value: 'actions', sortable: false, align: 'end',
        },
      ],
    }
  },
  computed: {
    // 来店処理（卓作成）は予約日=当営業日のときだけ
    businessToday() {
      const { currentClub } = useCurrentData()

      return getBusinessTodayStr(new Date(), currentClub.value?.openAt)
    },
    tableItems() {
      return this.reservations.map(res => {
        const a = res.attributes || {}
        const customers = a.customers || []
        const tableLabel = a.tableNumber
          ? `${a.hall ? a.hall.name : ''} ${a.tableNumber}`.trim()
          : '未指定'

        return {
          id: res.id,
          reservedDate: a.reservedDate,
          date: this.formatDate(a.reservedDate),
          time: this.timeRange(a),
          table: tableLabel,
          count: `${a.customerCount || 0}名`,
          nominated: this.nominationLabel(a),
          course: a.course ? a.course.name : '-',
          customers: this.customerNames(customers),
          status: a.status,
          statusLabel: STATUS_LABEL[a.status] || '',
          statusColor: STATUS_COLOR[a.status] || 'grey',
          sortKey: `${a.reservedDate || ''} ${a.startTime || ''}`,
        }
      })
    },
  },
  watch: {
    dateFrom() {
      this.fetchReservations()
    },
    dateTo() {
      this.fetchReservations()
    },
  },
  created() {
    this.fetchAll()
  },
  methods: {
    // 指名の表示（新形式の複数指名を優先、旧nominatedUserはフォールバック）
    nominationLabel(attrs) {
      const nominations = attrs.nominations || []
      if (nominations.length > 0) {
        return nominations.map(n => `${n.userName}(${n.nominationName})`).join('・')
      }

      return attrs.nominatedUser ? attrs.nominatedUser.name : '-'
    },
    todayOffset(days) {
      const d = new Date()
      d.setDate(d.getDate() + days)
      const y = d.getFullYear()
      const m = String(d.getMonth() + 1).padStart(2, '0')
      const day = String(d.getDate()).padStart(2, '0')

      return `${y}-${m}-${day}`
    },
    formatDate(dateStr) {
      if (!dateStr) return ''
      const d = new Date(`${dateStr}T00:00:00`)

      return `${d.getMonth() + 1}/${d.getDate()} (${WEEKDAYS[d.getDay()]})`
    },
    timeRange(a) {
      // start_time/end_time は "2000-01-01T20:00:00+09:00" 等のフル日時で返るため HH:MM を抽出
      const fmt = t => {
        if (!t) return ''
        const m = String(t).match(/(\d{2}):(\d{2})/)

        return m ? `${m[1]}:${m[2]}` : ''
      }

      return a.endTime ? `${fmt(a.startTime)}〜${fmt(a.endTime)}` : fmt(a.startTime)
    },
    customerNames(customers) {
      if (!customers || customers.length === 0) return '-'

      return customers[0].name + (customers.length > 1 ? ` 他${customers.length - 1}名` : '')
    },
    async fetchAll() {
      this.isLoading = true
      try {
        await this.fetchReservations()
      } finally {
        this.isLoading = false
      }
    },
    async fetchReservations() {
      try {
        const { data } = await ReservationApi.getReservations({
          from: this.dateFrom,
          to: this.dateTo,
        })
        this.reservations = data?.reservations?.data || data?.data || []
      } catch (e) {
        console.error('[ReservationList] fetch failed:', e)
      }
    },
    onRowClick(item) {
      // モーダルではなく、その日付の予約管理(タイムライン)へ遷移する
      this.$router.push({
        name: 'waiter-reservations',
        params: { club_slug: this.$route.params.club_slug },
        query: { date: item.reservedDate },
      })
    },
    goToTimeline(date) {
      this.gotoMenu = false
      // 指定日の予約管理(タイムライン)へ遷移して新規予約を作成できるようにする
      this.$router.push({
        name: 'waiter-reservations',
        params: { club_slug: this.$route.params.club_slug },
        query: { date },
      })
    },
    async checkIn(item) {
      if (item.reservedDate !== this.businessToday) {
        this.snackbarMessage = '来店処理は予約日（当営業日）にのみ実行できます'
        this.snackbar = true

        return
      }

      // 必須項目が不足している場合はブロックする（指名あり×コース未選択）
      const raw = this.reservations.find(r => r.id === item.id)
      const missing = checkInMissingItems(raw?.attributes || {})
      if (missing.length > 0) {
        this.snackbarMessage = `${missing.join('・')}が未入力のため来店処理できません。日別予約で予約を開いて入力してください`
        this.snackbar = true

        return
      }

      // eslint-disable-next-line no-alert
      if (!window.confirm('この予約から卓を作成しますか？')) return

      this.checkingInId = item.id
      try {
        await ReservationApi.checkInReservation(item.id)
        // 卓作成後はホール(卓一覧)へ遷移
        this.$router.push({
          name: 'waiter-halls',
          params: { club_slug: this.$route.params.club_slug },
        })
      } catch (e) {
        this.snackbarMessage = e.response?.data?.errors?.join(', ') || '卓作成に失敗しました'
        this.snackbar = true
      } finally {
        this.checkingInId = null
      }
    },
  },
}
</script>

<style scoped>
.gap-3 {
  gap: 12px;
}

::v-deep .v-data-table tbody tr {
  cursor: pointer;
}
</style>
